import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useMediaQuery } from '@podiumhq/design-system';

import {
  BUSINESS_FIELDS,
  validBusinessInfoDetails,
} from 'components/BusinessDetails';
import {
  BILLING_ADDRESS,
  BUSINESS_ADDRESS,
  SELECT_PLAN_BASE_ROUTE,
  TRIAL_BUY_NOW_BASE_ROUTE,
} from 'components/Checkout/constants';
import SelectPlanLoadingGhostView from 'components/SelectPlan/components/SelectPlanLoadingGhostView';
import { useLoadCheckoutData } from 'components/Checkout/hooks';
import useInitIpqs from 'components/ThirdPartyScripts/hooks/useInitIpqs';
import { getAllFormErrors, hasValidFields } from 'lib/fieldValidation';
import { getErrorRoute } from 'lib/getErrorRoute';
import useChangePage from '../hooks/useChangePage';

export const SelectPlanCheckoutContext = createContext({});

export const useSelectPlanCheckoutContext = () =>
  useContext(SelectPlanCheckoutContext);

const SelectPlanCheckoutContextProvider = ({ children, isFromTrial }) => {
  const changePage = useChangePage();
  const checkoutData = useLoadCheckoutData();
  const [isSmallerThan850px] = useMediaQuery('(max-width: 850px)');
  const navigate = useNavigate();

  const [billingAddress, setBillingAddress] = useState({});
  const [billingAddressErrors, setBillingAddressErrors] = useState({});
  const [billingErrorMessage, setBillingErrorMessage] = useState('');
  const [paymentRequestLoading, setPaymentRequestLoading] = useState(false);
  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState(BUSINESS_ADDRESS);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [setUpIntentData, setSetUpIntentData] = useState(null);
  const [stripeDetails, setStripeDetails] = useState({});
  const [stripeSetUpIntent, setStripeSetUpIntent] = useState(null);
  const [userDetailsErrors, setUserDetailsErrors] = useState({});

  const {
    businessAddress,
    dataLoading = true,
    isUserDataValid,
    userOrgDetailsError,
  } = checkoutData || {};

  useInitIpqs(isUserDataValid);

  const addressData = useMemo(
    () => ({
      billingAddress,
      billingAddressErrors,
      billingErrorMessage,
      businessAddress,
      hasNoAddress:
        !validBusinessInfoDetails(businessAddress) &&
        !validBusinessInfoDetails(billingAddress),
      selectedBillingAddress,
      setBillingAddress,
      setBillingAddressErrors,
      setBillingErrorMessage,
      setSelectedBillingAddress,
    }),
    [
      billingAddress,
      billingAddressErrors,
      billingErrorMessage,
      businessAddress,
      selectedBillingAddress,
    ]
  );

  const hasValidUserFields = useCallback((userFields, displayErrors = true) => {
    if (userFields) {
      const saveErrors = displayErrors && setUserDetailsErrors;
      return hasValidFields(userFields, saveErrors);
    } else {
      return false;
    }
  }, []);

  const validateBillingAddress = useCallback(
    ({
      addressToValidate = {},
      setErrors,
      trackEvent,
      optionalFields = ['name', 'phone'],
    } = {}) => {
      const billingErrors = getAllFormErrors({
        formFields: [...BUSINESS_FIELDS],
        formValues: addressToValidate,
        optionalFields,
        trackEvent,
        trackEventPath: 'selectPlanCheckout.billingAddress.fieldError',
      });
      const isValid = isEmpty(billingErrors);

      if (!isValid && setErrors) {
        setErrors(billingErrors);
      }

      return isValid;
    },
    []
  );

  const hasValidBillingAddress = useCallback(
    ({ trackEvent } = {}) => {
      if (addressData.hasNoAddress && isEmpty(billingAddress)) {
        setBillingErrorMessage('Please add a billing address.');
        return false;
      }

      if (selectedBillingAddress === BILLING_ADDRESS) {
        return validateBillingAddress({
          addressToValidate: billingAddress,
          setErrors: setBillingAddressErrors,
          trackEvent,
        });
      } else {
        return !isEmpty(businessAddress);
      }
    },
    [
      addressData.hasNoAddress,
      billingAddress,
      businessAddress,
      selectedBillingAddress,
      validateBillingAddress,
    ]
  );

  const checkoutRouting = useMemo(
    () => {
      const baseRoute = isFromTrial
        ? TRIAL_BUY_NOW_BASE_ROUTE
        : SELECT_PLAN_BASE_ROUTE;
      const changeCheckoutPage = (pageToChange) => changePage(pageToChange);

      return { baseRoute, changeCheckoutPage };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFromTrial]
  );

  useEffect(() => {
    // logic for handling redirection
    // <Navigate /> gave infinite loop in jest

    const invalidFromTrial =
      (isFromTrial && userOrgDetailsError) ||
      (isFromTrial && !dataLoading && !isUserDataValid);
    if (invalidFromTrial) {
      return navigate('/trials/business-info');
    }

    if (userOrgDetailsError) {
      return navigate(getErrorRoute(userOrgDetailsError?.message));
    }

    if (!dataLoading && !isUserDataValid) {
      return navigate('/not-found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, isFromTrial, isUserDataValid, userOrgDetailsError]);

  const selectPlanCheckoutData = useMemo(
    () => ({
      ...addressData,
      ...checkoutData,
      ...checkoutRouting,
      hasValidBillingAddress,
      hasValidUserFields,
      isMobile: isSmallerThan850px,
      paymentRequestLoading,
      selectedPlan,
      setPaymentRequestLoading,
      setSelectedPlan,
      setSetUpIntentData,
      setStripeDetails,
      setStripeSetUpIntent,
      setUpIntentData,
      setUserDetailsErrors,
      stripeDetails,
      stripeSetUpIntent,
      userDetailsErrors,
      validateBillingAddress,
    }),
    [
      addressData,
      checkoutData,
      checkoutRouting,
      hasValidBillingAddress,
      hasValidUserFields,
      isSmallerThan850px,
      paymentRequestLoading,
      selectedPlan,
      setUpIntentData,
      stripeDetails,
      stripeSetUpIntent,
      userDetailsErrors,
      validateBillingAddress,
    ]
  );

  return (
    <SelectPlanCheckoutContext.Provider value={selectPlanCheckoutData}>
      {!isFromTrial && (dataLoading || !isUserDataValid) ? (
        <SelectPlanLoadingGhostView isMobile={isSmallerThan850px} />
      ) : (
        children
      )}
    </SelectPlanCheckoutContext.Provider>
  );
};

export default SelectPlanCheckoutContextProvider;
